'use es6';
/*
 * Adds linting to CodeMirror instance.
 *
 * Passes annotations usable by `react-codemirror/decorator/annotations`
 */

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import ReactUtilsPropTypes from 'react-utils/CustomPropTypes';
import Annotation from 'react-codemirror/model/Annotation';
import { defaults, isEqual, uniqueId } from '../../../util/miniUnderscore.js';
import debounce from 'react-utils/debounce';
const EMPTY_ANNOTATIONS = Object.freeze([]);

function createAnnotations(propAnnotations, lintAnnotations) {
  if (propAnnotations.length && lintAnnotations.length) {
    return [].concat(propAnnotations, lintAnnotations);
  }

  if (propAnnotations.length) {
    return propAnnotations;
  }

  if (lintAnnotations.length) {
    return lintAnnotations;
  }

  return EMPTY_ANNOTATIONS;
}

const WORKER_CACHE = {};
const requestIdStore = new WeakMap();
export default function LintFactory(CodemirrorComponent, workerUrl, defaultLintOptions = {}) {
  /**
   * @class
   */
  class Lint extends Component {
    constructor(props) {
      super(props);
      requestIdStore.set(this, {
        prefix: uniqueId(),
        id: 0
      });
      this.propAnnotations = props.annotations || EMPTY_ANNOTATIONS;
      this.lintAnnotations = EMPTY_ANNOTATIONS;
      this.options = defaults({}, props.lintOptions, defaultLintOptions);
      this.state = {
        annotations: createAnnotations(this.propAnnotations, this.lintAnnotations)
      };
      this.validate = this.validate.bind(this);
      this.validateDebounced = debounce(this.validate, props.lintDebouncePeriod);
      this.onValidated = this.onValidated.bind(this);
      this.onChange = this.onChange.bind(this);
      this.refCodemirror = this.refCodemirror.bind(this);

      if (WORKER_CACHE[workerUrl]) {
        this.worker = WORKER_CACHE[workerUrl];
      } else {
        this.worker = new Worker(workerUrl);
        WORKER_CACHE[workerUrl] = this.worker;
      }

      if (props.enableLint) {
        this.bindWorkerListener();
      }
    }

    componentDidMount() {
      const {
        value
      } = this.props;

      if (value) {
        this.validate(value);
      }
    }

    componentDidUpdate(prevProps) {
      const {
        props
      } = this;
      let updateAnnotations = false;

      if ( // Linting options were changed
      prevProps.lintOptions !== props.lintOptions || // Linting was enabled
      !prevProps.enableLint && props.enableLint) {
        this.options = defaults({}, props.lintOptions, defaultLintOptions);

        if (this.codemirror) {
          this.validateDebounced(this.codemirror.getValue());
        }
      }

      if (!!prevProps.enableLint !== !!props.enableLint) {
        if (props.enableLint) {
          this.bindWorkerListener();
        } else {
          this.unbindWorkerListener();
          this.lintAnnotations = EMPTY_ANNOTATIONS;
          updateAnnotations = true;
        }
      } // New annotations received from parent component(s)


      if (prevProps.annotations !== props.annotations) {
        this.propAnnotations = props.annotations && props.annotations.length ? props.annotations : EMPTY_ANNOTATIONS;
        updateAnnotations = true;
      }

      if (updateAnnotations) {
        const annotations = createAnnotations(this.propAnnotations, this.lintAnnotations);
        this.setState({
          annotations
        });
      }

      if (prevProps.lintDebouncePeriod !== props.lintDebouncePeriod) {
        this.validateDebounced = debounce(this.validate, props.lintDebouncePeriod);
      }
    }

    componentWillUnmount() {
      this.unbindWorkerListener();
    }

    bindWorkerListener() {
      this.worker.addEventListener('message', this.onValidated);
    }

    unbindWorkerListener() {
      this.worker.removeEventListener('message', this.onValidated);
    }

    codemirrorDidMountOrUpdate(codemirror) {
      if (this.codemirror === codemirror) {
        return;
      }

      this.codemirror = codemirror;
    }

    get lintRequestId() {
      const {
        prefix,
        id
      } = requestIdStore.get(this);
      return `${prefix}:${id}`;
    }

    incrementLintRequestId() {
      ++requestIdStore.get(this).id;
      return this.lintRequestId;
    }

    validate(value) {
      if (!this.props.enableLint) {
        return;
      }

      const id = this.incrementLintRequestId();
      const {
        options
      } = this;
      this.worker.postMessage({
        id,
        value,
        options
      });
    }

    onValidated(event) {
      if (!this.props.enableLint) {
        return;
      }

      const {
        annotations,
        result,
        id
      } = JSON.parse(event.data);

      if (id === this.lintRequestId) {
        this.onLint(annotations, result);
      }
    }

    onLint(__lintAnnotations, apiResponse) {
      if (!this.props.enableLint) {
        return;
      }

      if (this.props.onLint) {
        this.props.onLint(__lintAnnotations, apiResponse);
      }

      if (__lintAnnotations.length === 0 && this.lintAnnotations.length === 0) {
        return;
      }

      if (isEqual(__lintAnnotations, this.lintAnnotations)) {
        return;
      }

      this.lintAnnotations = __lintAnnotations.length ? __lintAnnotations : EMPTY_ANNOTATIONS;
      const annotations = createAnnotations(this.propAnnotations, this.lintAnnotations);
      this.setState({
        annotations
      });
    }

    onChange(data) {
      this.validateDebounced(data.value);
      const {
        onChange
      } = this.props;

      if (onChange) {
        onChange(data);
      }
    }

    refCodemirror(ref) {
      const codemirror = ref && ref.codemirror;
      this.codemirrorDidMountOrUpdate(codemirror);

      if (this.props.refCodemirror) {
        this.props.refCodemirror(ref);
      }
    }

    render() {
      const _this$props = this.props,
            filteredProps = _objectWithoutPropertiesLoose(_this$props, ["enableLint", "onLint", "lintOptions", "lintDebouncePeriod"]);

      return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, filteredProps, {
        annotations: this.state.annotations,
        onChange: this.onChange,
        refCodemirror: this.refCodemirror
      }));
    }

  }
  /*
   * Statics
   */


  Lint.propTypes = {
    refCodemirror: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.string,
    annotations: Annotation.annotationsPropType,

    /**
     * Enables/disables linter.
     */
    enableLint: PropTypes.bool,

    /**
     * Optional callback fired on result from linter.
     * (annotations, linterResponse) => { }
     */
    onLint: PropTypes.func,

    /**
     * Optional configuration options sent to lint worker.
     * If defaultLintOptions is defined, this will be used as overrides.
     */
    lintOptions: PropTypes.object,

    /**
     * Debounce period to lint after changes to document are made.
     */
    lintDebouncePeriod: ReactUtilsPropTypes.uint32
  };
  Lint.defaultProps = {
    enableLint: false,
    lintDebouncePeriod: 500
  };
  Lint.displayName = 'Lint';
  return Lint;
}